import type {
  GamePlayerProjectionsAndScoresRequest,
  Status
} from '@library/types/projection';
import { useMemo, useState } from 'react';
import type { AutoTradeDrawerParams } from '@library/types/scoring';
import { AutoTradeHistoryDrawer } from '@bo-components/autoTradeHistoryDrawer/AutoTradeHistoryDrawer';
import { BulkAutoFinalizeButton } from '@bo-components/scoring/BulkAutoFinalizeButton';
import { BulkDNPButton } from '@bo-components/scoring/BulkDNPButton';
import { BulkIsFinalButton } from '@bo-components/scoring/BulkIsFinalButton';
import { ConfirmationModal } from '@library/components/modals/ConfirmationModal';
import { DEFAULT_SCORING_FILTERS } from '@bo-constants/filters';
import type { DateRange } from '@library/types/dates';
import { GlobalFilters } from '@library/components/GlobalFilters';
import { LeagueDependentFilters } from '@library/components/LeagueDependentFilters';
import { Loading } from '@library/components/Loading';
import { PageTitle } from '@library/components/PageTitle';
import { Scoring } from '@bo-components/scoring/Scoring';
import type { ScoringFilters } from '@library/types/filters';
import { Select } from '@library/components/selects';
import { getParameterValue } from '@library/component-utils/filters';
import { hasStandAloneOwnAttributes } from '@bo-utils/leagueAttributes';
import { removeDuplicatesInArray } from '@bo-utils/common';
import { useAutoTradeHistoryDrawerProps } from '@bo-hooks/autoTradeHistoryDrawer/useAutoTradeHistoryDrawerProps';
import { useConfirmationModal } from '@bo-hooks/useConfirmationModal';
import { useDashBoard } from '@bo-hooks/useDashboard';
import { useGames } from '@bo-hooks/useGames';
import { usePerPage } from '@bo-hooks/usePerPage';
import { useSentrySetDashBoardTag } from '@bo-hooks/sentry/useSentrySetDashBoardTag';
import { withProfiler } from '@sentry/react';

export const ScoringDashboardView = () => {
  useSentrySetDashBoardTag({ dashboardName: 'Scoring' });

  const {
    changeFilterValue,
    durations,
    filters,
    fromDate,
    handleMultiSelectDurations,
    handleSearch,
    handleSelectDateRange,
    handleSelectLeague,
    isLeagueLoading,
    leagueInfo,
    leaguesList,
    searchValue,
    selectedDurations,
    selectedEndDate,
    selectedLeaguesDurationAttributes,
    selectedStartDate,
    setFilters,
    toDate
  } = useDashBoard({
    dashboardName: 'SCORING'
  });

  const [currentPage, setCurrentPage] = useState(1);

  const { id: leagueId, name } = leagueInfo ?? {};

  const {
    dnp: selectedDNP,
    games: selectedGames,
    gameModes: selectedGameModeIds,
    reboot: selectedReboot,
    statType: selectedStatType,
    status: selectedStatus,
    teams: selectedTeams
  } = filters as ScoringFilters;

  const statTypes = selectedLeaguesDurationAttributes
    .map((leagueDurationAttributes) => leagueDurationAttributes.statTypes)
    .flat();

  const allStats = removeDuplicatesInArray(statTypes);

  const standAloneAttributes = hasStandAloneOwnAttributes({ name });
  const leagueIds = useMemo(() => {
    return standAloneAttributes
      ? [leagueId ?? '']
      : selectedLeaguesDurationAttributes.map(
          (leagueDurationAttributes) => leagueDurationAttributes.leagueId
        );
  }, [standAloneAttributes, leagueId, selectedLeaguesDurationAttributes]);

  const handleReset = () => {
    setFilters(DEFAULT_SCORING_FILTERS);
    handleSearch('');
    setCurrentPage(1);
  };

  const handleChangeLeague = (newLeagueId: string) => {
    handleSelectLeague(newLeagueId);
    handleReset();
  };

  const handleChangeDate = (value: DateRange) => {
    handleSelectDateRange(value);
    handleReset();
  };

  // Sending the parent/base leagueId since games are same across the leagues/sub leagues
  // this would avoid multiple api calls when the duration/sub league is changed
  const { games } = useGames({
    leagueId,
    fromDate,
    toDate
  });

  const [disabledFilters, setDisabledFilters] = useState(false);

  const { handleSelectPerPage, perPageOptions, selectedPerPage } = usePerPage();

  const {
    drawerOpen: autoTradeDrawerOpen,
    durationId: autoTradeDurationId,
    gameDescriptor: autoTradeGameDescriptor,
    gameId: autoTradeGameId,
    handleSelectAutoTradeHistory,
    leagueId: autoTradeLeagueId,
    onCancelAutoTradeHistoryDrawerModal,
    playerId: autoTradePlayerId,
    playerImage: autoTradePlayerImage,
    playerName: autoTradePlayerName,
    position: autoTradePosition
  } = useAutoTradeHistoryDrawerProps();

  const {
    confirmationModalProps,
    handleSubmit,
    resetConfirmationModal,
    setConfirmationModalProps
  } = useConfirmationModal();

  const scoringQueryParams =
    useMemo<GamePlayerProjectionsAndScoresRequest>(() => {
      return {
        createdByUserId: undefined,
        durationId: standAloneAttributes ? selectedDurations : [],
        leagueId: leagueIds,
        fromDate,
        gameId: getParameterValue(selectedGames),
        gameModeIds: getParameterValue(selectedGameModeIds),
        search: searchValue,
        statTypeIds: getParameterValue(selectedStatType),
        status: getParameterValue<Array<Status>>(selectedStatus),
        team: getParameterValue(selectedTeams),
        toDate
      };
    }, [
      toDate,
      leagueIds,
      searchValue,
      selectedDurations,
      selectedGameModeIds,
      selectedGames,
      selectedStatType,
      selectedStatus,
      selectedTeams,
      standAloneAttributes,
      fromDate
    ]);

  const handleShowHistoryDrawer = (params: AutoTradeDrawerParams) => {
    handleSelectAutoTradeHistory(params);
  };

  if (isLeagueLoading) {
    return <Loading loadingMessage="Loading Leagues" />;
  }

  return (
    <div className="w-auto px-6 pb-6 pt-3">
      <PageTitle title="Scoring" />
      <div className="flex flex-col gap-4 pt-4">
        <GlobalFilters
          allowPastDates
          disabled={disabledFilters}
          durations={durations}
          handleMultiSelectDurations={handleMultiSelectDurations}
          handleSearch={handleSearch}
          handleSelectDateRange={handleChangeDate}
          handleSelectLeague={handleChangeLeague}
          searchDisabled={disabledFilters}
          searchValue={searchValue}
          leaguesList={leaguesList}
          selectedDurations={selectedDurations}
          selectedEndDate={selectedEndDate}
          selectedStartDate={selectedStartDate}
          selectedLeagueId={leagueId}
        />
        <div className="flex w-full justify-between">
          <LeagueDependentFilters
            className="flex flex-wrap items-center gap-4"
            disabled={disabledFilters}
            filters={filters}
            games={games}
            handleDNPChange={(value: string) => {
              changeFilterValue('dnp', value);
              setCurrentPage(1);
            }}
            handleGamesChange={(value: Array<string>) => {
              changeFilterValue('games', value);
              setCurrentPage(1);
            }}
            handleGameModesChange={(value: Array<string>) => {
              changeFilterValue('gameModes', value);
              setCurrentPage(1);
            }}
            handleStatTypeChange={(value: Array<string>) => {
              changeFilterValue('statType', value);
              setCurrentPage(1);
            }}
            handleTeamsChange={(value: Array<string>) => {
              changeFilterValue('teams', value);
              setCurrentPage(1);
            }}
            handleStatusChange={(value: Array<string>) => {
              changeFilterValue('status', value);
              setCurrentPage(1);
            }}
            handleRebootChange={(value: string) => {
              changeFilterValue('reboot', value);
              setCurrentPage(1);
            }}
            onReset={handleReset}
            statTypeValues={allStats}
          />
        </div>
        <div className="mb-3 flex items-center gap-4">
          <BulkAutoFinalizeButton
            setConfirmationModalProps={setConfirmationModalProps}
            scoringQueryParams={scoringQueryParams}
          />
          <BulkIsFinalButton
            setConfirmationModalProps={setConfirmationModalProps}
            scoringQueryParams={scoringQueryParams}
          />
          <BulkDNPButton
            setConfirmationModalProps={setConfirmationModalProps}
            scoringQueryParams={scoringQueryParams}
          />
          <div className="flex items-center gap-4">
            <span className="text-soClean-100 self-center text-sm">
              Rows Per Page:
            </span>
            <Select
              onChange={handleSelectPerPage}
              options={perPageOptions}
              label="Per Page"
              size="small"
              value={selectedPerPage}
              testId="scoring-rows-per-page"
            />
          </div>
        </div>
      </div>
      {confirmationModalProps && (
        <ConfirmationModal
          confirmationCopy={confirmationModalProps.confirmationCopy}
          onCancelModal={resetConfirmationModal}
          handleConfirm={handleSubmit}
          label={confirmationModalProps.label}
          isOpen
          isConfirmDisabled={confirmationModalProps.isConfirmDisabled}
          isLoading={confirmationModalProps.isLoading}
        >
          {confirmationModalProps.children}
        </ConfirmationModal>
      )}
      <Scoring
        currentPage={currentPage}
        disabledFilters={disabledFilters}
        dnp={selectedDNP}
        durationId={selectedDurations}
        fromDate={fromDate}
        games={selectedGames}
        gameModeIds={selectedGameModeIds.map((gameMode) => Number(gameMode))}
        handleShowHistoryDrawer={handleShowHistoryDrawer}
        leagueId={leagueIds}
        leagueInfo={leagueInfo}
        perPage={selectedPerPage}
        reboot={selectedReboot}
        status={selectedStatus}
        teams={selectedTeams}
        statTypeIds={selectedStatType}
        searchValue={searchValue}
        setCurrentPage={setCurrentPage}
        setDisabledFilters={setDisabledFilters}
        toDate={toDate}
      />
      <AutoTradeHistoryDrawer
        drawerOpen={autoTradeDrawerOpen}
        durationId={autoTradeDurationId}
        fromDateString={fromDate}
        gameDescriptor={autoTradeGameDescriptor}
        gameId={autoTradeGameId}
        leagueId={autoTradeLeagueId}
        onCancelDrawer={onCancelAutoTradeHistoryDrawerModal}
        playerId={autoTradePlayerId}
        playerImage={autoTradePlayerImage}
        playerName={autoTradePlayerName}
        position={autoTradePosition}
      />
    </div>
  );
};

export const ScoringDashboard = withProfiler(ScoringDashboardView);
